import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, Ban, AlertCircle, Loader2, RotateCcw, ArrowLeft, Home } from 'lucide-react';

export const formatGs = (n) => `Gs. ${Number(n || 0).toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('es-PY', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Asuncion' });
};

const VARIANTS = {
  success: { Icon: CheckCircle2, ring: 'bg-[#DCE9E2]', color: 'text-[#264639]', title: '¡Pago aprobado!',
    text: 'Tu pago fue confirmado por Bancard y tu reserva quedó confirmada. Te enviamos un correo con los detalles.' },
  failed: { Icon: XCircle, ring: 'bg-[#FEE2E2]', color: 'text-[#991B1B]', title: 'Pago no aprobado',
    text: 'Bancard no aprobó el pago. Podés intentar nuevamente con otra tarjeta.' },
  pending: { Icon: Clock, ring: 'bg-[#FEF3C7]', color: 'text-[#B45309]', title: 'Verificación en proceso',
    text: 'Todavía no recibimos la confirmación de Bancard. Si ya pagaste, se confirmará automáticamente en unos minutos.' },
  cancelled: { Icon: Ban, ring: 'bg-[#F5F4F0]', color: 'text-[#57534E]', title: 'Pago cancelado',
    text: 'No se realizó ningún cobro. Podés volver a pagar cuando quieras desde la propiedad o tus reservas.' },
  error: { Icon: AlertCircle, ring: 'bg-[#FEE2E2]', color: 'text-[#991B1B]', title: 'Error en el pago', text: '' },
};

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[#78716C]">{label}</span>
      <span className="font-medium text-[#1C1917] dark:text-white text-right" data-testid={testId}>{value}</span>
    </div>
  );
}

/** Result screen. Per Bancard doc: show date/time, order nº (shop_process_id), amount and response description only. */
export function BancardResult({ variant, status, errorText, propertyId, onRetry, onRecheck, retrying }) {
  const v = VARIANTS[variant] || VARIANTS.error;
  const Icon = v.Icon;
  const backTo = propertyId ? `/properties/${propertyId}` : '/properties';
  return (
    <div className="max-w-md w-full bg-white dark:bg-[#1C1917] rounded-2xl shadow-xl p-8 text-center" data-testid={`bancard-result-${variant}`}>
      <div className={`w-20 h-20 ${v.ring} rounded-full flex items-center justify-center mx-auto mb-6`}>
        <Icon className={`w-11 h-11 ${v.color}`} />
      </div>
      <h2 className="text-2xl font-bold text-[#1C1917] dark:text-white mb-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>{v.title}</h2>
      <p className="text-sm text-[#57534E] dark:text-[#A8A29E] mb-6" data-testid="bancard-result-text">{errorText || v.text}</p>

      {status?.shop_process_id && (
        <div className="bg-[#F5F4F0] dark:bg-[#0C0A09] rounded-xl p-4 mb-6 text-left space-y-2" data-testid="bancard-result-details">
          <Row label="Fecha y hora" value={formatDateTime(status.confirmed_at || status.created_at)} testId="bancard-result-date" />
          <Row label="Nº de pedido" value={status.shop_process_id} testId="bancard-result-order" />
          <Row label="Importe" value={formatGs(status.amount)} testId="bancard-result-amount" />
          {status.response_description && <Row label="Respuesta" value={status.response_description} testId="bancard-result-description" />}
          {status.metadata?.property_title && <Row label="Alojamiento" value={status.metadata.property_title} testId="bancard-result-property" />}
        </div>
      )}

      <div className="space-y-3">
        {variant === 'success' && (
          <>
            <Link to="/dashboard" className="block w-full px-6 py-3 bg-[#264639] text-white rounded-xl hover:bg-[#1a3328] transition-colors font-semibold" data-testid="bancard-go-dashboard">
              Ver mis reservas
            </Link>
            <Link to="/properties" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#E7E5E4] rounded-xl text-[#1C1917] dark:text-white hover:bg-[#F5F4F0] dark:hover:bg-[#292524] transition-colors" data-testid="bancard-explore">
              <Home className="w-4 h-4" /> Explorar más propiedades
            </Link>
          </>
        )}
        {(variant === 'failed' || variant === 'error') && onRetry && (
          <button onClick={onRetry} disabled={retrying}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#264639] text-white rounded-xl hover:bg-[#1a3328] transition-colors font-semibold disabled:opacity-60"
            data-testid="bancard-retry-btn">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Intentar con otra tarjeta
          </button>
        )}
        {variant === 'pending' && onRecheck && (
          <button onClick={onRecheck} disabled={retrying}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#264639] text-white rounded-xl hover:bg-[#1a3328] transition-colors font-semibold disabled:opacity-60"
            data-testid="bancard-recheck-btn">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Volver a consultar
          </button>
        )}
        {variant !== 'success' && (
          <Link to={backTo} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#E7E5E4] rounded-xl text-[#1C1917] dark:text-white hover:bg-[#F5F4F0] dark:hover:bg-[#292524] transition-colors" data-testid="bancard-back-btn">
            <ArrowLeft className="w-4 h-4" /> Volver a la propiedad
          </Link>
        )}
      </div>
    </div>
  );
}
